import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';

export interface CanonicalCourse {
  id: string;
  moodle_course_id: number;
  fullname: string | null;
  shortname: string | null;
  summary: string | null;
  category_id: number | null;
  category_name?: string | null;
  category_path?: string | null;
  visible: boolean;
  last_synced_at: string | null;
  /** Aktiv eingeschriebene Lernende — ohne Lehrende, ohne suspendierte oder
   *  ausgetragene Einschreibungen. Kam vom Endpunkt schon immer mit, war nur
   *  nie typisiert. */
  participant_count?: number;
  /** Unsere eigene courses.id, sofern dieser Moodle-Kurs gespiegelt ist.
   *  Verantwortliche und Grundlagenverknüpfungen hängen daran, nicht am
   *  kanonischen Moodle-Kurs. */
  local_course_id?: string | null;
  responsibles?: { user_id: string; name: string; source: string }[];
}

export interface CanonicalUser {
  id: string;
  learner_id?: string | null;
  moodle_user_id: number;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  enrollment_count: number;
  last_synced_at: string | null;
}

export interface CanonicalTeacher {
  id: string;
  moodle_user_id: number;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  role_shortnames: string[];
  course_count: number;
  courses: Array<{
    moodle_course_uuid: string;
    moodle_course_id: number;
    course_fullname: string | null;
    course_shortname: string | null;
    role_shortnames: string[];
  }>;
  last_synced_at: string | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  total?: number;
  count?: number;
  limit?: number;
  offset?: number;
  has_more?: boolean;
  pagination?: { total: number; limit: number; offset: number };
  categories?: Array<{
    key?: string;
    registration_id?: string;
    id: number;
    name?: string | null;
    path?: string | null;
    label?: string | null;
    count?: number;
  }>;
}

export interface CanonicalEnrollment {
  id: string;
  moodle_user_uuid: string;
  moodle_user_id: number;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  role: string | null;
  role_shortnames?: string[];
  status: string | null;
  last_synced_at: string | null;
}

export interface CanonicalGrade {
  item_name: string | null;
  item_type: string | null;
  raw_grade: number | null;
  grade_max: number | null;
  grade_formatted: string | null;
  feedback: string | null;
}

export interface UserCourseRow {
  enrollment_id: string;
  moodle_course_uuid: string;
  moodle_course_id: number;
  course_fullname: string | null;
  course_shortname: string | null;
  role: string | null;
  role_shortnames?: string[];
  status: string | null;
  last_synced_at: string | null;
  grades: CanonicalGrade[];
}

export interface MoodleRefreshTrigger {
  registration_id: string;
  queued_kinds: string[];
  next_due_at: string;
  refreshed_kinds?: string[];
  course_meta_stats?: Record<string, number | string | null>;
}

export interface MoodleRefreshStatus {
  state: 'idle' | 'running' | 'running_with_errors' | 'completed' | 'completed_with_errors';
  total: number;
  done: number;
  failed: number;
  pending: number;
  running: number;
  queued: number;
  percent: number;
  started_at?: string | null;
  by_kind?: Record<string, {
    total: number;
    done: number;
    failed: number;
    running: number;
    queued: number;
  }>;
}

@Injectable({ providedIn: 'root' })
export class MoodleDashboardService {
  private base = `${environment.tutorsBackend}/moodle/canonical`;

  constructor(private http: HttpClient) {}

  private params(
    registrationId?: string | null,
    opts?: {
      limit?: number;
      offset?: number;
      search?: string;
      categoryKey?: string | null;
      categoryId?: number | null;
    },
  ): HttpParams {
    let params = new HttpParams();
    if (registrationId) params = params.set('registration_id', registrationId);
    if (opts?.limit != null) params = params.set('limit', String(opts.limit));
    if (opts?.offset != null) params = params.set('offset', String(opts.offset));
    if (opts?.search) params = params.set('search', opts.search);
    if (opts?.categoryKey) params = params.set('category_key', opts.categoryKey);
    if (opts?.categoryId != null) params = params.set('category_id', String(opts.categoryId));
    return params;
  }

  listCourses(
    registrationId?: string | null,
    opts?: {
      limit?: number;
      offset?: number;
      search?: string;
      categoryKey?: string | null;
      categoryId?: number | null;
    },
  ): Observable<PaginatedResponse<CanonicalCourse>> {
    return this.http
      .get<PaginatedResponse<CanonicalCourse>>(`${this.base}/courses`, {
        params: this.params(registrationId, opts),
      });
  }

  refreshCanonical(registrationId?: string | null): Observable<MoodleRefreshTrigger | {
    queued: MoodleRefreshTrigger[];
    registration_count: number;
  }> {
    const url = registrationId
      ? `${this.base}/refresh/${registrationId}`
      : `${this.base}/refresh`;
    return this.http.post<MoodleRefreshTrigger | {
      queued: MoodleRefreshTrigger[];
      registration_count: number;
    }>(url, {});
  }

  getRefreshStatus(
    registrationId?: string | null,
    since?: string | null,
  ): Observable<MoodleRefreshStatus> {
    let params = new HttpParams();
    if (registrationId) params = params.set('registration_id', registrationId);
    if (since) params = params.set('since', since);
    return this.http.get<MoodleRefreshStatus>(`${this.base}/refresh-status`, { params });
  }

  listCourseEnrollments(
    courseUuid: string,
    opts?: { limit?: number; offset?: number; search?: string },
  ): Observable<PaginatedResponse<CanonicalEnrollment>> {
    return this.http
      .get<PaginatedResponse<CanonicalEnrollment>>(
        `${this.base}/courses/${courseUuid}/enrollments`,
        { params: this.params(null, opts) },
      );
  }

  listCourseGrades(courseUuid: string): Observable<any[]> {
    return this.http
      .get<{ data: any[] }>(`${this.base}/courses/${courseUuid}/grades`)
      .pipe(map((r) => r.data || []));
  }

  listUsers(
    registrationId?: string | null,
    opts?: { limit?: number; offset?: number; search?: string },
  ): Observable<PaginatedResponse<CanonicalUser>> {
    return this.http
      .get<PaginatedResponse<CanonicalUser>>(`${this.base}/users`, {
        params: this.params(registrationId, opts),
      });
  }

  listTeachers(
    registrationId?: string | null,
    opts?: { limit?: number; offset?: number; search?: string },
  ): Observable<PaginatedResponse<CanonicalTeacher>> {
    return this.http
      .get<PaginatedResponse<CanonicalTeacher>>(`${this.base}/teachers`, {
        params: this.params(registrationId, opts),
      });
  }

  listUserEnrollments(userUuid: string): Observable<UserCourseRow[]> {
    return this.http
      .get<{ user: any; data: UserCourseRow[] }>(`${this.base}/users/${userUuid}/enrollments`)
      .pipe(map((r) => r.data || []));
  }

  // ── Dozent course-access (master Moodle dashboard, super_admin) ──

  listAssignableDozents(): Observable<{ id: string; name: string; email: string }[]> {
    return this.http
      .get<{ data: any[] }>(`${this.base}/assignable-dozents`)
      .pipe(map((r) => r.data || []));
  }

  listCourseDozentAccess(
    courseUuid: string,
  ): Observable<{ user_id: string; user_name: string; user_email: string }[]> {
    return this.http
      .get<{ data: any[] }>(`${this.base}/courses/${courseUuid}/dozent-access`)
      .pipe(map((r) => r.data || []));
  }

  replaceCourseDozentAccess(courseUuid: string, userIds: string[]): Observable<any> {
    return this.http.put(`${this.base}/courses/${courseUuid}/dozent-access`, {
      user_ids: userIds,
    });
  }
}
